import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import axios from 'axios';
import {
  Zap, Search, MessageSquare, Users, LogOut, Menu, Filter,
  ExternalLink, Sparkles, TrendingUp, ChevronRight, Loader2,
  LayoutDashboard, Briefcase, RefreshCw, MapPin, Building2, DollarSign, Mic, Phone,
  BarChart3, Workflow, Activity, Store,
} from 'lucide-react';
import { HSLogo } from '@/components/HSLogo';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Jobs = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('data entry');
  const [searchLocation, setSearchLocation] = useState('');
  const [category, setCategory] = useState('all');
  const [sidebarOpen, setSidebarOpen] = useState(false);

  useEffect(() => {
    fetchSavedJobs();
  }, []);

  const fetchSavedJobs = async () => {
    try {
      const res = await axios.get(`${API}/jobs`);
      setJobs(res.data);
    } catch (err) {
      console.error('Failed to fetch jobs:', err);
    }
  };

  const handleSearch = async (e) => {
    e?.preventDefault();
    setLoading(true);
    try {
      const res = await axios.post(`${API}/jobs/search`, {
        query: searchQuery,
        location: searchLocation,
        category: category
      });
      setJobs(res.data);
      toast.success(`Found ${res.data.length} job opportunities!`);
    } catch (err) {
      toast.error('Failed to search jobs');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateLead = async (jobId) => {
    try {
      await axios.post(`${API}/leads?job_lead_id=${jobId}`);
      toast.success('Lead added to CRM!');
      fetchSavedJobs();
    } catch (err) {
      toast.error('Failed to create lead');
    }
  };

  const handleGeneratePitch = (job) => {
    navigate('/pitch', { state: { job } });
  };

  const navItems = [
    { icon: LayoutDashboard, label: 'Dashboard', path: '/dashboard' },
    { icon: Workflow, label: 'Workflow Builder', path: '/workflow' },
    { icon: BarChart3, label: 'Demand Intel', path: '/demand' },
    { icon: Search, label: 'Job Leads', path: '/jobs' },
    { icon: Users, label: 'Outbound Leads', path: '/crm' },
    { icon: MessageSquare, label: 'Pitch Generator', path: '/pitch' },
    { icon: Store, label: 'Marketplace', path: '/marketplace' },
    { icon: Activity, label: 'API Hub', path: '/hub' },
  ];

  const categories = [
    { value: 'all', label: 'All Categories' },
    // General
    { value: 'data_entry', label: 'Data Entry' },
    { value: 'admin', label: 'Admin Assistant' },
    { value: 'customer_support', label: 'Customer Support' },
    { value: 'email_management', label: 'Email Management' },
    { value: 'scheduling', label: 'Scheduling' },
    { value: 'social_media', label: 'Social Media' },
    { value: 'lead_gen', label: 'Lead Generation' },
    // Construction
    { value: 'roofing_estimator', label: 'Roofing Estimator' },
    { value: 'production_scheduler', label: 'Production Scheduler' },
    { value: 'material_takeoff', label: 'Material Takeoff' },
    { value: 'voice_receptionist', label: 'Voice Receptionist' },
    // Insurance
    { value: 'insurance_supplement', label: 'Insurance Supplement' },
    { value: 'claims_data_entry', label: 'Claims Data Entry' },
    // Healthcare
    { value: 'medical_receptionist', label: 'Medical Receptionist' },
    { value: 'appointment_scheduler', label: 'Appointment Scheduler' },
    { value: 'patient_intake', label: 'Patient Intake' },
    // Real Estate
    { value: 'lead_qualifier', label: 'Lead Qualifier' },
    { value: 'showing_scheduler', label: 'Showing Scheduler' },
    { value: 'transaction_coordinator', label: 'Transaction Coordinator' },
  ];

  const getCategoryLabel = (cat) => {
    const found = categories.find(c => c.value === cat);
    return found ? found.label : cat;
  };

  const getScoreColor = (score) => {
    if (score >= 90) return 'text-emerald-400';
    if (score >= 75) return 'text-amber-400';
    return 'text-muted-foreground';
  };

  return (
    <div className="min-h-screen bg-[#050505] flex">
      <div className="noise-overlay" />
      
      {/* Sidebar */}
      <aside className={`fixed lg:static inset-y-0 left-0 z-50 w-64 bg-[#0a0a0a] border-r border-white/5 transform transition-transform lg:translate-x-0 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        <div className="flex flex-col h-full">
          <div className="p-6 border-b border-white/5">
            <Link to="/" className="flex items-center gap-2">
              <div className="w-8 h-8 bg-emerald-500 flex items-center justify-center">
                <Zap className="w-5 h-5 text-black" />
              </div>
              <span className="font-black text-lg tracking-tight">HumanScaled</span>
            </Link>
          </div>

          <nav className="flex-1 p-4 space-y-1">
            {navItems.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`flex items-center gap-3 px-4 py-3 rounded-sm transition-colors ${
                    isActive 
                      ? 'bg-emerald-500/10 text-emerald-400 border-l-2 border-emerald-500' 
                      : 'text-muted-foreground hover:text-white hover:bg-white/5'
                  }`}
                >
                  <Icon className="w-5 h-5" />
                  {item.label}
                </Link>
              );
            })}
          </nav>

          <div className="p-4 border-t border-white/5">
            <div className="flex items-center gap-3 px-4 py-3 bg-white/5 rounded-sm mb-3">
              <div className="w-8 h-8 bg-emerald-500/20 flex items-center justify-center text-emerald-400 font-bold text-sm">
                {user?.name?.charAt(0) || 'U'}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{user?.name}</p>
                <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
              </div>
            </div>
            <Button
              variant="ghost"
              onClick={() => { logout(); navigate('/'); }}
              className="w-full justify-start text-muted-foreground hover:text-white"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Sign Out
            </Button>
          </div>
        </div>
      </aside>

      {sidebarOpen && (
        <div className="fixed inset-0 bg-black/50 z-40 lg:hidden" onClick={() => setSidebarOpen(false)} />
      )}

      {/* Main content */}
      <main className="flex-1 min-h-screen">
        <header className="sticky top-0 z-30 bg-[#050505]/80 backdrop-blur-md border-b border-white/5">
          <div className="flex items-center justify-between h-16 px-6">
            <button onClick={() => setSidebarOpen(true)} className="lg:hidden p-2 text-muted-foreground hover:text-white">
              <Menu className="w-6 h-6" />
            </button>
            <h1 className="text-lg font-bold">Job Leads</h1>
            <span className="text-xs font-mono px-2 py-1 bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">
              {jobs.length} JOBS
            </span>
          </div>
        </header>

        <div className="p-6 lg:p-8 space-y-6">
          {/* Search bar */}
          <form onSubmit={handleSearch} className="bg-[#0a0a0a] border border-white/5 p-6">
            <div className="grid md:grid-cols-4 gap-4">
              <div className="md:col-span-1">
                <label className="text-xs text-muted-foreground uppercase tracking-wide mb-2 block">Search</label>
                <Input
                  placeholder="Job title, keywords..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="h-12 bg-black/50 border-white/10 focus:border-emerald-500 rounded-sm"
                  data-testid="job-search-input"
                />
              </div>
              <div className="md:col-span-1">
                <label className="text-xs text-muted-foreground uppercase tracking-wide mb-2 block">Location</label>
                <Input
                  placeholder="City, state, or remote"
                  value={searchLocation}
                  onChange={(e) => setSearchLocation(e.target.value)}
                  className="h-12 bg-black/50 border-white/10 focus:border-emerald-500 rounded-sm"
                  data-testid="job-location-input"
                />
              </div>
              <div className="md:col-span-1">
                <label className="text-xs text-muted-foreground uppercase tracking-wide mb-2 block">Category</label>
                <Select value={category} onValueChange={setCategory}>
                  <SelectTrigger className="h-12 bg-black/50 border-white/10 rounded-sm" data-testid="job-category-select">
                    <SelectValue placeholder="All categories" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0a0a0a] border-white/10">
                    {categories.map(cat => (
                      <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="md:col-span-1 flex items-end">
                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full h-12 bg-emerald-500 text-black hover:bg-emerald-400 font-bold rounded-sm"
                  data-testid="job-search-btn"
                >
                  {loading ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <><Search className="w-5 h-5 mr-2" /> Search Jobs</>
                  )}
                </Button>
              </div>
            </div>
          </form>

          {/* Source summary */}
          {jobs.length > 0 && (
            <div className="flex flex-wrap items-center gap-3" data-testid="source-summary">
              {(() => {
                const counts = {};
                jobs.forEach(j => { counts[j.source || 'demo'] = (counts[j.source || 'demo'] || 0) + 1; });
                return Object.entries(counts).map(([src, count]) => (
                  <span key={src} className={`text-xs font-mono px-2 py-1 ${
                    src === 'remotive' ? 'bg-blue-500/10 text-blue-400 border border-blue-500/20' :
                    src === 'arbeitnow' ? 'bg-cyan-500/10 text-cyan-400 border border-cyan-500/20' :
                    src === 'indeed' ? 'bg-orange-500/10 text-orange-400 border border-orange-500/20' :
                    src === 'jsearch' ? 'bg-green-500/10 text-green-400 border border-green-500/20' :
                    'bg-white/5 text-muted-foreground border border-white/10'
                  }`}>
                    {src === 'demo' ? 'SAMPLE' : src === 'jsearch' ? 'GOOGLE JOBS' : src.toUpperCase()}: {count}
                  </span>
                ));
              })()}
              <span className="text-xs text-muted-foreground">
                Live results from multiple job boards
              </span>
            </div>
          )}

          {/* Results */}
          <div className="space-y-4">
            {jobs.length > 0 ? jobs.map((job, i) => (
              <div 
                key={job.id || i}
                className={`bg-[#0a0a0a] border p-6 hover:border-emerald-500/30 transition-all group relative ${job.voice_ai ? 'border-violet-500/30' : 'border-white/5'}`}
                data-testid={`job-card-${i}`}
              >
                {job.voice_ai && (
                  <div className="absolute -top-2 -left-2 px-2 py-0.5 bg-violet-500 text-white text-xs font-bold flex items-center gap-1">
                    <Mic className="w-3 h-3" /> VOICE AI
                  </div>
                )}
                <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-start gap-3 mb-3">
                      <div className={`w-10 h-10 flex items-center justify-center flex-shrink-0 ${job.voice_ai ? 'bg-violet-500/10' : 'bg-white/5'}`}>
                        {job.voice_ai ? <Phone className="w-5 h-5 text-violet-400" /> : <Building2 className="w-5 h-5 text-muted-foreground" />}
                      </div>
                      <div>
                        <h3 className="font-bold text-lg group-hover:text-emerald-400 transition-colors">{job.title}</h3>
                        <p className="text-muted-foreground">{job.company}</p>
                      </div>
                    </div>
                    
                    <div className="flex flex-wrap gap-4 text-sm text-muted-foreground mb-4">
                      <span className="flex items-center gap-1">
                        <MapPin className="w-4 h-4" /> {job.location}
                      </span>
                      {job.salary_range && (
                        <span className="flex items-center gap-1">
                          <DollarSign className="w-4 h-4" /> {job.salary_range}
                        </span>
                      )}
                      <span className="px-2 py-0.5 bg-white/5 font-mono text-xs uppercase">
                        {getCategoryLabel(job.category)}
                      </span>
                      {job.industry && job.industry !== 'general' && (
                        <span className="px-2 py-0.5 bg-emerald-500/10 text-emerald-400 font-mono text-xs uppercase">
                          {job.industry}
                        </span>
                      )}
                      <span className={`px-2 py-0.5 font-mono text-xs uppercase ${
                        job.source === 'remotive' ? 'bg-blue-500/10 text-blue-400 border border-blue-500/20' :
                        job.source === 'arbeitnow' ? 'bg-cyan-500/10 text-cyan-400 border border-cyan-500/20' :
                        job.source === 'indeed' ? 'bg-orange-500/10 text-orange-400 border border-orange-500/20' :
                        job.source === 'jsearch' ? 'bg-green-500/10 text-green-400 border border-green-500/20' :
                        'bg-white/5 text-muted-foreground border border-white/10'
                      }`} data-testid={`job-source-${i}`}>
                        {job.source === 'demo' ? 'sample' : job.source === 'jsearch' ? 'google jobs' : job.source || 'sample'}
                      </span>
                    </div>

                    <p className="text-sm text-muted-foreground line-clamp-2 mb-4">{job.description}</p>

                    <div className="flex items-center gap-2 text-sm">
                      <Sparkles className="w-4 h-4 text-violet-400" />
                      <span className="text-muted-foreground">{job.pitch_angle}</span>
                    </div>
                  </div>

                  <div className="lg:w-64 space-y-4">
                    {/* Score card */}
                    <div className="bg-black/40 p-4 border border-white/5">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-xs text-muted-foreground uppercase">AI Replaceability</span>
                        <span className={`font-mono font-bold text-lg ${getScoreColor(job.ai_replaceability_score)}`}>
                          {job.ai_replaceability_score}%
                        </span>
                      </div>
                      <div className="w-full h-2 bg-white/10">
                        <div 
                          className="h-full bg-emerald-500 transition-all"
                          style={{ width: `${job.ai_replaceability_score}%` }}
                        />
                      </div>
                      <p className="text-xs text-muted-foreground mt-2 font-mono">
                        {job.estimated_savings}
                      </p>
                    </div>

                    {/* Actions */}
                    <div className="flex gap-2">
                      <Button
                        onClick={() => handleGeneratePitch(job)}
                        className="flex-1 bg-emerald-500 text-black hover:bg-emerald-400 font-bold rounded-sm h-10"
                        data-testid={`generate-pitch-btn-${i}`}
                      >
                        <MessageSquare className="w-4 h-4 mr-1" /> Pitch
                      </Button>
                      <Button
                        onClick={() => handleCreateLead(job.id)}
                        variant="outline"
                        className="flex-1 border-white/10 hover:border-emerald-500 rounded-sm h-10"
                        disabled={job.status === 'pitched'}
                        data-testid={`add-lead-btn-${i}`}
                      >
                        <TrendingUp className="w-4 h-4 mr-1" /> {job.status === 'pitched' ? 'Added' : 'Add Lead'}
                      </Button>
                    </div>
                    
                    <a
                      href={job.source_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center justify-center gap-2 text-sm text-muted-foreground hover:text-emerald-400 transition-colors"
                    >
                      View on Indeed <ExternalLink className="w-4 h-4" />
                    </a>
                  </div>
                </div>
              </div>
            )) : (
              <div className="bg-[#0a0a0a] border border-white/5 p-12 text-center">
                <Search className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-50" />
                <h3 className="text-lg font-bold mb-2">No jobs found</h3>
                <p className="text-muted-foreground mb-4">
                  Search Indeed to find job opportunities ripe for AI automation.
                </p>
                <Button
                  onClick={handleSearch}
                  className="bg-emerald-500 text-black hover:bg-emerald-400 font-bold rounded-sm"
                  data-testid="empty-search-btn"
                >
                  <Search className="w-4 h-4 mr-2" /> Search Now
                </Button>
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default Jobs;